// ConectividadBadge — estado de la señal de una línea conectada a máquina.
//
//   <ConectividadBadge conectividad={l.conectividad} ultimoLatidoAt={l.ultimo_latido_at} />
//
// Con `conectividad` null/undefined (línea manual) NO renderiza nada: las
// pantallas de tenants sin máquinas quedan idénticas. Solo usa tokens, así que
// funciona igual en el tema oscuro de /pantalla (data-theme="dark").
//
// Es un indicador SEPARADO del estado de producción (semáforo, paro): una línea
// puede estar en verde y sin señal, o con señal y en paro. Nunca se mezclan.

import type { ConectividadLinea } from 'schemas';
import { cn } from '../../lib/utils';
import { tiempoRelativo } from '../../lib/tiempoRelativo';
import { Badge } from './badge';
import { Icon, type IconName } from './icon';

type Tono = 'ok' | 'warn' | 'crit';

const META: Record<ConectividadLinea, { label: string; tone: Tono; icon: IconName }> = {
  en_linea: { label: 'En línea', tone: 'ok', icon: 'wifi' },
  recuperando: { label: 'Recuperando', tone: 'warn', icon: 'refresh' },
  sin_senal: { label: 'Sin señal', tone: 'crit', icon: 'wifi-off' },
};

/** Etiqueta y tono de un estado de conectividad (null = línea manual). */
export function conectividadInfo(
  conectividad: ConectividadLinea | null | undefined,
): { label: string; tone: Tono; icon: IconName } | null {
  if (!conectividad) return null;
  return META[conectividad];
}

/**
 * Etiqueta legible + descripción del último latido, para badges, tooltips y
 * lectores de pantalla. Devuelve null en líneas manuales.
 */
export function useConectividadLabel(
  conectividad: ConectividadLinea | null | undefined,
  ultimoLatidoAt?: string | null,
): { label: string; descripcion: string } | null {
  const info = conectividadInfo(conectividad);
  if (!info) return null;
  const latido =
    ultimoLatidoAt === undefined
      ? null
      : ultimoLatidoAt === null
        ? 'La máquina aún no ha enviado latidos'
        : `Último latido ${tiempoRelativo(ultimoLatidoAt)}`;
  return {
    label: info.label,
    descripcion: latido
      ? `Máquina: ${info.label.toLowerCase()}. ${latido}`
      : `Máquina: ${info.label.toLowerCase()}`,
  };
}

export interface ConectividadBadgeProps {
  conectividad: ConectividadLinea | null | undefined;
  ultimoLatidoAt?: string | null;
  /** `compact` = solo icono (tablas densas); el texto queda en title/aria-label. */
  compact?: boolean;
  className?: string;
}

export function ConectividadBadge({
  conectividad,
  ultimoLatidoAt,
  compact = false,
  className,
}: ConectividadBadgeProps) {
  const info = conectividadInfo(conectividad);
  const textos = useConectividadLabel(conectividad, ultimoLatidoAt);
  if (!info || !textos) return null;
  return (
    <Badge
      tone={info.tone}
      title={textos.descripcion}
      aria-label={textos.descripcion}
      data-conectividad={conectividad}
      className={cn(
        'shrink-0 text-xs normal-case tracking-normal',
        compact ? 'px-1.5' : '',
        className,
      )}
    >
      <Icon
        name={info.icon}
        size={12}
        stroke={2}
        className={cn(
          conectividad === 'recuperando'
            ? 'motion-safe:animate-spin [animation-duration:2.4s]'
            : '',
        )}
      />
      {compact ? null : <span>{info.label}</span>}
    </Badge>
  );
}
